import {List} from '@dropbox/dig-components/list';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {TeamLine} from '@dropbox/dig-icons/assets';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import {$findMatchingParent} from '@lexical/utils';
import {snackbarAtom} from 'atoms/snackbar';
import {Employee, EmployeeRoleLevel, TeamWithCounts} from 'client';
import {Avatar, AvatarIcon} from 'components/DSYS/Avatar';
import {usePlanningId} from 'components/DSYS/CollabProvider';
import {$createMentionNode, $isMentionNode} from 'components/DSYS/editor/nodes/MentionNode';
import {setMentionMenuOpen} from 'components/DSYS/editor/utils/mentionMenuState';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {isEmployee, isTeamSearchResult, useGlobalSearch} from 'hooks/useEmployee';
import {t} from 'i18next';
import {useSetAtom} from 'jotai';
import {
  $createParagraphNode,
  $createRangeSelection,
  $createTextNode,
  $getRoot,
  $isElementNode,
  $isLineBreakNode,
  $isTextNode,
  $setSelection,
  LexicalNode,
  TextNode,
} from 'lexical';
import {useCallback, useEffect, useMemo, useState} from 'react';
import * as ReactDOM from 'react-dom';
import {useNotifyForMention} from 'views/planning/hooks';

import styles from './Mention.module.css';

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = '\\b[A-Z][^\\s' + PUNCTUATION + ']';

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ['@'].join('');

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = '[^' + TRIGGERS + PUNC + '\\s]';

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  '[' +
  PUNC +
  ']|' + // E.g. "-' in "Salier-Hellendag"
  ')';

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    VALID_JOINS +
    '){0,' +
    LENGTH_LIMIT +
    '})' +
    ')$'
);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  '(^|\\s|\\()(' +
    '[' +
    TRIGGERS +
    ']' +
    '((?:' +
    VALID_CHARS +
    '){0,' +
    ALIAS_LENGTH_LIMIT +
    '})' +
    ')$'
);

const DOCUMENT_MENTION_PREVIEW_MAX_LENGTH = 200;

const normalizeMentionPreviewWhitespace = (text: string) => text.replace(/\s+/g, ' ').trim();

const getMentionPreviewText = (node: LexicalNode): string => {
  if ($isMentionNode(node)) {
    return `@${node.getTrigger()}`;
  }

  if ($isTextNode(node)) {
    return node.getTextContent();
  }

  if ($isLineBreakNode(node)) {
    return '\n';
  }

  if ($isElementNode(node)) {
    return node
      .getChildren()
      .map((childNode) => getMentionPreviewText(childNode))
      .join('');
  }

  return '';
};

const buildDocumentMentionPreview = (mentionNode: LexicalNode): string | undefined => {
  const previewContainer =
    $findMatchingParent(
      mentionNode,
      (node) => $isElementNode(node) && !node.isInline() && node.getType() !== 'root'
    ) ?? mentionNode.getParent();

  if (!previewContainer) {
    return undefined;
  }

  const previewText = normalizeMentionPreviewWhitespace(getMentionPreviewText(previewContainer));
  if (!previewText) {
    return undefined;
  }

  return previewText.slice(0, DOCUMENT_MENTION_PREVIEW_MAX_LENGTH);
};

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignMentions(text, 1);
}

export class MentionTypeaheadOption extends MenuOption {
  id: string;
  slug: string;
  name: string;
  subtext: string;
  type: 'employee' | 'team';

  constructor(key: string) {
    super(key);
    this.id = key;
    this.slug = key;
    this.name = '';
    this.subtext = '';
    this.type = 'employee';
  }
}

export class EmployeeMentionTypeaheadOption extends MentionTypeaheadOption {
  constructor(employee: EmployeeRoleLevel) {
    super(employee.ldap);
    this.id = employee.user_id;
    this.slug = employee.ldap;
    this.name = employee.name;
    this.subtext = employee.role ?? '';
    this.type = 'employee';
  }
}

export class TeamMentionTypeaheadOption extends MentionTypeaheadOption {
  constructor(team: TeamWithCounts) {
    super(team.slug ?? '');
    this.id = team.team_id;
    this.slug = team.slug ?? '';
    this.name = team.name ?? '';
    this.subtext = `${t('member', {count: team.members_count ?? 0, countString: team.members_count ?? 0})}`;
    this.type = 'team';
  }
}

export const MentionsTypeaheadMenuItem = ({
  index,
  isSelected,
  onClick,
  onMouseEnter,
  option,
}: {
  index: number;
  isSelected: boolean;
  onClick: () => void;
  onMouseEnter: () => void;
  option: MentionTypeaheadOption;
}) => {
  return (
    <List.Item
      key={option.key}
      tabIndex={-1}
      isSelected={isSelected}
      ref={option.setRefElement}
      role="option"
      aria-selected={isSelected}
      id={'typeahead-item-' + index}
      onMouseEnter={onMouseEnter}
      onMouseDown={(event) => {
        // Keep Lexical focused until onClick replaces the active typeahead match.
        event.preventDefault();
      }}
      onClick={onClick}
      className={atoms({borderRadius: 'Medium'})}
    >
      <LabelGroup
        withLeftAccessory={
          option.type === 'employee' ? (
            <Avatar
              user={{
                user_id: option.id,
                ldap: option.slug,
                name: option.name,
              }}
            />
          ) : (
            <AvatarIcon src={TeamLine} size="small" />
          )
        }
        withText={option.name}
        withSubtext={
          <div
            style={{
              maxWidth: 316,
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {option.subtext}
          </div>
        }
      />

      {/* <Split alignY="center" gap="8">
        <Split.Item>
          {option.type === 'employee' ? (
            <Avatar
              user={{
                user_id: option.id,
                ldap: option.slug,
                name: option.name,
              }}
              size="small"
            />
          ) : (
            <AvatarIcon src={TeamLine} size="small" />
          )}
        </Split.Item>
        <Split.Item>
          <Text>{option.name}</Text>
        </Split.Item>
      </Split> */}
    </List.Item>
  );
};

export const NewMentionsPlugin = ({
  mentions,
  autoMentionTag,
  documentSynced,
  peopleOnly = false,
}: {
  mentions: MentionTypeaheadOption[];
  autoMentionTag?: MentionTypeaheadOption[];
  documentSynced?: boolean;
  /** Suggest people only, and never notify the person mentioned (e.g. Ask). */
  peopleOnly?: boolean;
}) => {
  const [editor] = useLexicalComposerContext();
  const [queryString, setQueryString] = useState<string | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const searchQuery = queryString?.trim() ?? null;
  const {data: results} = useGlobalSearch({
    input: searchQuery,
    filter: peopleOnly ? 'people' : ('people,teams' as any),
  });

  const contextPlanningId = usePlanningId();
  const planningId = peopleOnly ? undefined : contextPlanningId;
  const {mutate: notifyForMention} = useNotifyForMention();
  const setSnackbarMessage = useSetAtom(snackbarAtom);

  useEffect(() => {
    if (!documentSynced) return;

    // Check for mentionNodeId in URL
    const url = new URL(window.location.href);
    const mentionNodeId = url.searchParams.get('mention');
    if (!mentionNodeId) return;

    const mentionEl = document.querySelector(`[data-mention-node-id="${mentionNodeId}"]`);
    if (mentionEl) {
      mentionEl.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }, [documentSynced]);

  const checkForAtTriggerMatch = useBasicTypeaheadTriggerMatch('@', {
    minLength: 0,
  });

  useEffect(() => {
    if (autoMentionTag) {
      editor.update(() => {
        const root = $getRoot();

        const paragraphNode = $createParagraphNode();
        autoMentionTag.forEach((mention) => {
          const mentionNode = $createMentionNode(
            mention.name,
            mention.slug,
            mention.type,
            mention.id
          );
          paragraphNode.append(mentionNode);
          paragraphNode.append($createTextNode(' '));
        });
        root.append(paragraphNode);

        // Move cursor to the end of the newly created content
        const lastChild = paragraphNode.getLastChild();
        if (lastChild) {
          const selection = $createRangeSelection();
          selection.anchor.set(lastChild.getKey(), lastChild.getTextContentSize(), 'text');
          selection.focus.set(lastChild.getKey(), lastChild.getTextContentSize(), 'text');
          $setSelection(selection);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(() => {
    if (searchQuery?.length && !results.length) {
      return [];
    }

    if (!results.length) {
      return mentions;
    }

    return (
      results
        .filter(
          (result): result is Employee | TeamWithCounts =>
            result !== undefined &&
            (isEmployee(result) || (!peopleOnly && isTeamSearchResult(result)))
        )
        .map((result) => {
          if (isEmployee(result)) {
            return new EmployeeMentionTypeaheadOption(result);
          } else if (isTeamSearchResult(result)) {
            return new TeamMentionTypeaheadOption(result);
          }
          return null;
        })
        .filter(
          (option): option is EmployeeMentionTypeaheadOption | TeamMentionTypeaheadOption =>
            option !== null
        )
        // The people fallback (shown for a bare "@") can list someone twice.
        .filter((option, i, all) => all.findIndex(({key}) => key === option.key) === i)
        .slice(0, 5)
    );
  }, [mentions, peopleOnly, searchQuery?.length, results]);

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void
    ) => {
      editor.update(() => {
        if (nodeToReplace) {
          const mentionNode = $createMentionNode(
            selectedOption.name,
            selectedOption.slug,
            selectedOption.type,
            selectedOption.id
          );
          nodeToReplace.replace(mentionNode);

          // Insert a space after the mention only if there isn't already one
          const nextSibling = mentionNode.getNextSibling();
          if (
            !nextSibling ||
            (nextSibling.getType() === 'text' && nextSibling.getTextContent() !== ' ')
          ) {
            const spaceNode = $createTextNode(' ');
            mentionNode.insertAfter(spaceNode);
          }

          // Notify user for mention in planning docs
          if (planningId) {
            setSnackbarMessage({
              text: t('mention_added_snackbar', {mention_name: selectedOption.name}),
            });
            const commentPreview = buildDocumentMentionPreview(mentionNode);
            notifyForMention({
              planningId,
              employeeId: selectedOption.id,
              mentionNodeId: mentionNode.getMentionNodeId(),
              commentPreview,
            });
          }
        }
        closeMenu();
      });
    },
    [editor, planningId, notifyForMention, setSnackbarMessage]
  );

  // Enter and Tab pick a person while the menu shows any (see mentionMenuState).
  const showsPeople = isMenuOpen && options.length > 0;
  useEffect(() => {
    setMentionMenuOpen(editor, showsPeople);
    return () => setMentionMenuOpen(editor, false);
  }, [editor, showsPeople]);

  const checkForMentionMatch = useCallback(
    (text: string) => {
      return getPossibleQueryMatch(text) || checkForAtTriggerMatch(text, editor);
    },
    [checkForAtTriggerMatch, editor]
  );

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onOpen={() => setIsMenuOpen(true)}
      onClose={() => setIsMenuOpen(false)}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      anchorClassName={styles.container}
      menuRenderFn={(
        anchorElementRef,
        {selectedIndex, selectOptionAndCleanUp, setHighlightedIndex}
      ) =>
        anchorElementRef.current
          ? ReactDOM.createPortal(
              <List
                spacing="xsmall"
                isSelectable
                style={{
                  width: 400,
                  maxHeight: 256,
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                }}
                className={atoms({
                  borderRadius: 'Medium',
                  borderColor: 'Border Subtle',
                  backgroundColor: 'Background Raised',
                  boxShadow: 'Floating',
                  borderWidth: '1',
                  borderStyle: 'Solid',
                  padding: '8',
                })}
              >
                {options?.length ? (
                  options.map((option, i: number) => (
                    <MentionsTypeaheadMenuItem
                      index={i}
                      isSelected={selectedIndex === i}
                      onClick={() => {
                        setHighlightedIndex(i);
                        selectOptionAndCleanUp(option);
                      }}
                      onMouseEnter={() => {
                        setHighlightedIndex(i);
                      }}
                      key={option.key}
                      option={option}
                    />
                  ))
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" paddingY="8">
                    <Text color="disabled">{t('no_dropboxer')}</Text>
                  </Box>
                )}
              </List>,
              anchorElementRef.current
            )
          : null
      }
    />
  );
};
